var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React from 'react';
import { createUseStyles } from 'react-jss';
import { useTheme } from '../../theme';
import { useMemoClasses } from '../../utils/hooks';
var useStyles = createUseStyles({
    primary: {
        '--RUI-avatar-main': 'var(--RUI-color-primary)',
        '--RUI-avatar-contrast': 'var(--RUI-color-primary-contrast)',
    },
    secondary: {
        '--RUI-avatar-main': 'var(--RUI-color-secondary)',
        '--RUI-avatar-contrast': 'var(--RUI-color-secondary-contrast)',
    },
    neutral: {
        '--RUI-avatar-main': 'var(--RUI-color-neutral)',
        '--RUI-avatar-contrast': 'var(--RUI-color-neutral-contrast)',
    },
    error: {
        '--RUI-avatar-main': 'var(--RUI-color-error)',
        '--RUI-avatar-contrast': 'var(--RUI-color-error-contrast)',
    },
    warning: {
        '--RUI-avatar-main': 'var(--RUI-color-warning)',
        '--RUI-avatar-contrast': 'var(--RUI-color-warning-contrast)',
    },
    success: {
        '--RUI-avatar-main': 'var(--RUI-color-success)',
        '--RUI-avatar-contrast': 'var(--RUI-color-success-contrast)',
    },
    small: {
        width: '25px',
        height: '25px',
        fontSize: '4px',
    },
    smallOverrides: function (_a) {
        var theme = _a.theme;
        return (__assign({}, theme.overrides.Avatar.small));
    },
    normal: {
        width: '50px',
        height: '50px',
        fontSize: '8px',
    },
    normalOverrides: function (_a) {
        var theme = _a.theme;
        return (__assign({}, theme.overrides.Avatar.normal));
    },
    big: {
        width: '75px',
        height: '75px',
        fontSize: '12px',
    },
    bigOverrides: function (_a) {
        var theme = _a.theme;
        return (__assign({}, theme.overrides.Avatar.big));
    },
    base: {
        position: 'relative',
        display: 'inline-block',
        margin: 'var(--RUI-margin)',
    },
    baseOverrides: function (_a) {
        var theme = _a.theme;
        return (__assign({}, theme.overrides.Avatar.base));
    },
    img: {
        verticalAlign: 'middle',
        width: '100%',
        height: '100%',
        borderRadius: '50%',
        objectFit: 'cover',
    },
    imgOverrides: function (_a) {
        var theme = _a.theme;
        return (__assign({}, theme.overrides.Avatar.img));
    },
    notification: {
        position: 'absolute',
        backgroundColor: 'var(--RUI-avatar-main)',
        borderRadius: '50%',
        opacity: '1',
        width: '30%',
        height: '30%',
        border: 'var(--RUI-border-width) solid var(--RUI-avatar-contrast)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        pointerEvents: 'none',
        color: 'var(--RUI-avatar-contrast)',
    },
    notificationOverrides: function (_a) {
        var theme = _a.theme;
        return (__assign({}, theme.overrides.Avatar.notification));
    },
    topLeft: {
        top: 0,
        left: 0,
    },
    topLeftOverrides: function (_a) {
        var theme = _a.theme;
        return (__assign({}, theme.overrides.Avatar.topLeft));
    },
    topRight: {
        top: 0,
        right: 0,
    },
    topRightOverrides: function (_a) {
        var theme = _a.theme;
        return (__assign({}, theme.overrides.Avatar.topRight));
    },
    bottomLeft: {
        bottom: 0,
        left: 0,
    },
    bottomLeftOverrides: function (_a) {
        var theme = _a.theme;
        return (__assign({}, theme.overrides.Avatar.bottomLeft));
    },
    bottomRight: {
        bottom: 0,
        right: 0,
    },
    bottomRightOverrides: function (_a) {
        var theme = _a.theme;
        return (__assign({}, theme.overrides.Avatar.bottomRight));
    },
}, { name: 'RUIAvatar' });
var Avatar = function (_a) {
    var className = _a.className, notification = _a.notification, notificationNumber = _a.notificationNumber, notificationColor = _a.notificationColor, notificationPosition = _a.notificationPosition, size = _a.size, style = _a.style, props = __rest(_a, ["className", "notification", "notificationNumber", "notificationColor", "notificationPosition", "size", "style"]);
    var theme = useTheme();
    var styles = useStyles({ theme: theme });
    var baseClasses = useMemoClasses([
        styles.base,
        styles[size],
        theme.overrides.Avatar.small && styles.smallOverrides,
        theme.overrides.Avatar.normal && styles.normalOverrides,
        theme.overrides.Avatar.big && styles.bigOverrides,
        theme.overrides.Avatar.base && styles.baseOverrides,
        'RUIAvatar-base',
        className,
    ], [theme, className, size]);
    var imgClasses = useMemoClasses([styles.img, theme.overrides.Avatar.img && styles.imgOverrides, 'RUIAvatar-img'], [theme]);
    var notificationClasses = useMemoClasses([
        styles.notification,
        styles[notificationColor],
        notificationPosition === 'top-left' && styles.topLeft,
        notificationPosition === 'top-right' && styles.topRight,
        notificationPosition === 'bottom-left' && styles.bottomLeft,
        notificationPosition === 'bottom-right' && styles.bottomRight,
        theme.overrides.Avatar.notification && styles.notificationOverrides,
        notificationPosition === 'top-left' && theme.overrides.Avatar.topLeft && styles.topLeftOverrides,
        notificationPosition === 'top-right' && theme.overrides.Avatar.topRight && styles.topRightOverrides,
        notificationPosition === 'bottom-left' && theme.overrides.Avatar.bottomLeft && styles.bottomLeftOverrides,
        notificationPosition === 'bottom-right' && theme.overrides.Avatar.bottomRight && styles.bottomRightOverrides,
        'RUIAvatar-notification',
    ], [theme, notificationColor]);
    return (React.createElement("div", { style: style, className: baseClasses },
        React.createElement("img", __assign({ className: imgClasses }, props)),
        notification && (React.createElement("label", { className: notificationClasses }, notificationNumber > 99 ? '99+' : notificationNumber))));
};
Avatar.defaultProps = {
    notification: false,
    notificationPosition: 'top-left',
    notificationColor: 'primary',
    notificationNumber: null,
    size: 'normal',
};
export { Avatar as default, Avatar };
